import { useNavigate } from "react-router-dom"
import { AuthService } from "../services/auth.service"
import { useEffect, useState } from "react"
import { customSwalStyling, DataService } from "../services/data.service"
import { MainLayout } from "./MainLayout"
import { ListModel } from "../models/list.model"
import { UserModel } from "../models/user.model"
import { Beneficiary } from "./Beneficiary"
import { useLogout } from "../hooks/logout.hook"
import { Card } from "./ui/card"
import { LayoutDashboard, RefreshCcw, Search } from "lucide-react"
import { PageTitle } from "./PageTitle"
import { debugModeActive, isNotInMobileApp} from "../lib/utils"
import Swal from "sweetalert2"

// Post-auth name capture (PLAN.md §3 question 1).
// Triggered once on first dashboard visit after isNewUser=true OR
// profileComplete=false. The user can save or skip; either path clears
// the local needsProfile flag so the prompt does not re-fire.
async function maybePromptForName() {
    if (!AuthService.hasAuth()) return
    const auth = AuthService.getAuth()
    if (!auth.needsProfile) return

    const result = await Swal.fire({
        title: "Welcome to WellNuo",
        html: `
            <p class="text-sm text-gray-500 mb-3">Tell us your name so we can address you properly.</p>
            <input id="np-first" class="swal2-input" placeholder="First name" autocomplete="given-name" />
            <input id="np-last" class="swal2-input" placeholder="Last name" autocomplete="family-name" />
        `,
        showCancelButton: true,
        confirmButtonText: "Save",
        cancelButtonText: "Skip",
        focusConfirm: false,
        customClass: customSwalStyling,
        preConfirm: () => {
            const first = (document.getElementById('np-first') as HTMLInputElement)?.value?.trim() || ''
            const last = (document.getElementById('np-last') as HTMLInputElement)?.value?.trim() || ''
            if (!first || !last) {
                Swal.showValidationMessage('Please enter both first and last name')
                return false
            }
            return { first, last }
        }
    })

    if (result.isConfirmed && result.value) {
        try {
            await DataService.updateProfile(result.value.first, result.value.last)
        } catch (err: any) {
            Swal.fire({ icon: "error", title: "Could not save", text: err?.message || 'Please try again later', customClass: customSwalStyling })
            return // Leave needsProfile set so the next visit re-prompts.
        }
    } else {
        // Skipped — clear the flag locally so we don't re-prompt every visit.
        AuthService.updateAuth({ needsProfile: false })
    }
}

export function Dashboard() {
    const navigate = useNavigate()
    const logout = useLogout()
    const [users, setUsers] = useState<ListModel<UserModel>>()
    const [timeSinceLastUpdate, setTimeSinceLastUpdate] = useState(0)
    const [searchQuery, setSearchQuery] = useState('')

    useEffect(() => {
        if (!AuthService.hasAuth()) {
            navigate('/login', { replace: true })
            return
        }

        // Fire-and-forget: name capture runs in parallel with data load.
        maybePromptForName()

        loadData()
        const interval = setInterval(() => {
            loadData(true)
        }, 15 * 60 * 1000)

        const timer = setInterval(() => {
            setTimeSinceLastUpdate(prev => prev + 1)
        }, 1000)

        return () => {
            clearInterval(interval)
            clearInterval(timer)
        }
    }, [])

    function loadData(silent = false) {
        DataService.getUsers(silent)
            .then(data => {
                setTimeSinceLastUpdate(0)
                setUsers(data)
            })
            .catch(e => logout())
    }

    function openDetails(id: number) {
        navigate('/dashboard/' + id, { replace: true })
    }

    return (
        <MainLayout>
            {isNotInMobileApp() &&
                <PageTitle>
                    <LayoutDashboard className="text-3xl text-gray-800" size={32} />
                    <h1 className="text-2xl font-bold text-gray-800">Dashboard</h1>
                </PageTitle>
            }

            <div className="space-y-2">
                {users &&
                    <Card className="p-4 shadow-md rounded-lg border">
                        <div className="text-sm flex justify-center items-center gap-2">
                            <p>
                                {Math.floor(timeSinceLastUpdate / 60)}m {timeSinceLastUpdate % 60}s since last report
                            </p>
                            <RefreshCcw
                                className="cursor-pointer"
                                onClick={() => loadData()}
                            />
                        </div>
                        {debugModeActive() &&
                            <div className="text-center text-sm text-gray-400">Build Version: {process.env.REACT_APP_VERSION}</div>
                        }
                    </Card>
                }

                {users && users.result_list.length > 3 && (
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                        <input
                            type="text"
                            placeholder="Filter by name or email..."
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-800 placeholder:text-gray-400 focus:border-blue-400 focus:outline-none"
                        />
                    </div>
                )}

                {users && users.result_list
                    .filter(user => {
                        if (!searchQuery.trim()) return true
                        const q = searchQuery.toLowerCase()
                        return user.name.toLowerCase().includes(q) ||
                            (user.nickname || '').toLowerCase().includes(q) ||
                            (user.email || '').toLowerCase().includes(q) ||
                            (user.address || '').toLowerCase().includes(q)
                    })
                    .map((user, index) => (
                    <div key={index} onClick={e => openDetails(user.deployment_id)}>
                        <Beneficiary user={user} />
                    </div>
                ))}
            </div>
        </MainLayout>
    )
}